import { useState, useCallback, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOutboundPackageListQuery } from 'api/warehouse';
import type { OutboundPackage, OutboundFilters } from 'models/Warehouse';
import { WAREHOUSE_ROUTES } from 'constants/routes';

interface UseOutboundPackagesProps {
  searchValue?: string;
  filters?: OutboundFilters;
}

export const useOutboundPackages = ({ searchValue = '', filters = {} }: UseOutboundPackagesProps) => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [allPackages, setAllPackages] = useState<OutboundPackage[]>([]);
  const pageSize = 10;

  const { data, isLoading, isFetching } = useOutboundPackageListQuery({
    page,
    size: pageSize,
    search: searchValue || undefined,
    filters,
  });

  // Stable reference for filters to use in dependency array
  const filtersRef = useMemo(() => JSON.stringify(filters), [filters]);

  // Reset state on mount
  useEffect(() => {
    setPage(1);
    setAllPackages([]);
  }, []);

  // Reset page and packages when search or filters change
  useEffect(() => {
    setPage(1);
    setAllPackages([]);
  }, [searchValue, filtersRef]);

  // Update all packages when new data arrives
  useEffect(() => {
    if (data?.packages) {
      setAllPackages((prev) => {
        if (page === 1) {
          return data.packages;
        }
        // Avoid duplicates
        const existingIds = new Set(prev.map((p) => p.id));
        const newPackages = data.packages.filter((p) => !existingIds.has(p.id));
        return [...prev, ...newPackages];
      });
    }
  }, [data, page]);

  const hasMore = useMemo(() => {
    if (!data) return false;
    return allPackages.length < data.total;
  }, [allPackages.length, data]);

  const loadMore = useCallback(() => {
    if (!isFetching && hasMore) {
      setPage((prev) => prev + 1);
    }
  }, [isFetching, hasMore]);

  const handleCardClick = useCallback(
    (id: string) => {
      navigate(WAREHOUSE_ROUTES.outboundOrderDetails.replace(':id', id));
    },
    [navigate]
  );

  return {
    allPackages,
    isLoading,
    page,
    hasMore,
    loadMore,
    handleCardClick,
  };
};
